import React, { useEffect, useRef, useState } from 'react';

/**
 * CustomCursor — premium acid yellow circle that follows the mouse.
 *
 *  - Outer ring lerps toward the mouse for buttery motion.
 *  - Inner dot tracks the mouse 1:1.
 *  - On hover over `.magnetic`, `a`, `button`, `[role="button"]`, the ring
 *    enlarges and turns acid.
 *  - On `.magnetic` elements the cursor's parent button gets a tiny pull
 *    toward the mouse (magnetic effect).
 *  - Auto-hides on touch devices.
 */
export default function CustomCursor() {
  const ringRef = useRef(null);
  const dotRef = useRef(null);
  const rafRef = useRef(0);
  const mouse = useRef({ x: -100, y: -100 });
  const ring = useRef({ x: -100, y: -100 });
  const [enabled, setEnabled] = useState(false);
  const [hovering, setHovering] = useState(false);

  useEffect(() => {
    // Always set the host class — the `@media (pointer:fine)` rule in CSS gates
    // the actual `cursor: none`. On touch / coarse devices the CSS no-ops.
    document.documentElement.classList.add('modaria-cursor-host');

    // Detect touch / coarse pointer — disable JS cursor entirely there.
    const isCoarse = window.matchMedia('(pointer: coarse)').matches;
    if (isCoarse) {
      return () => {
        document.documentElement.classList.remove('modaria-cursor-host');
      };
    }
    setEnabled(true);

    const onMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      }

      // Magnetic pull on hovered .magnetic ancestors
      const target = e.target.closest?.('.magnetic');
      document.querySelectorAll('.magnetic[data-magnetic-active="1"]').forEach((el) => {
        if (el !== target) {
          el.style.transform = '';
          el.removeAttribute('data-magnetic-active');
        }
      });
      if (target) {
        const rect = target.getBoundingClientRect();
        const dx = (e.clientX - (rect.left + rect.width / 2)) * 0.18;
        const dy = (e.clientY - (rect.top + rect.height / 2)) * 0.18;
        target.style.transform = `translate(${dx}px, ${dy}px)`;
        target.style.transition = 'transform 220ms cubic-bezier(.2,.8,.2,1)';
        target.setAttribute('data-magnetic-active', '1');
      }
    };

    const onOver = (e) => {
      const t = e.target.closest?.('button, a, [role="button"], .magnetic, input, textarea');
      setHovering(!!t);
    };

    const onLeave = () => {
      mouse.current.x = -100;
      mouse.current.y = -100;
    };

    const tick = () => {
      ring.current.x += (mouse.current.x - ring.current.x) * 0.18;
      ring.current.y += (mouse.current.y - ring.current.y) * 0.18;
      if (ringRef.current) {
        ringRef.current.style.transform =
          `translate3d(${ring.current.x}px, ${ring.current.y}px, 0)`;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseover', onOver);
    document.addEventListener('mouseleave', onLeave);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseleave', onLeave);
      cancelAnimationFrame(rafRef.current);
      document.documentElement.classList.remove('modaria-cursor-host');
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <div
        ref={ringRef}
        data-testid="custom-cursor-ring"
        aria-hidden
        className={`pointer-events-none fixed top-0 left-0 z-[9999] -translate-x-1/2 -translate-y-1/2 rounded-full transition-[width,height,background-color,border-color,opacity] duration-200 ${
          hovering
            ? 'w-12 h-12 border-0 bg-acid/25 mix-blend-difference'
            : 'w-8 h-8 border border-acid/70 bg-transparent'
        }`}
        style={{
          marginLeft: hovering ? '-24px' : '-16px',
          marginTop: hovering ? '-24px' : '-16px',
        }}
      />
      <div
        ref={dotRef}
        data-testid="custom-cursor-dot"
        aria-hidden
        className="pointer-events-none fixed top-0 left-0 z-[9999] w-1.5 h-1.5 rounded-full bg-acid"
        style={{ marginLeft: '-3px', marginTop: '-3px' }}
      />
    </>
  );
}
